import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Brain, ShieldCheck, Activity, Sparkles } from "lucide-react";

const BRAIN_IMG =
  "https://images.unsplash.com/photo-1617791160536-598cf32026fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxicmFpbiUyMG5ldXJhbCUyMG5ldHdvcmslMjBhYnN0cmFjdCUyMHZpc3VhbCUyMGNvZ25pdGl2ZSUyMHRlc3RpbmclMjBzdHVkaW98ZW58MHx8fHwxNzkxOTI2MzEzfDA&ixlib=rb-4.1.0&q=85";

export const Landing = () => {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/instructions";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen bg-[#0B0F17] overflow-hidden grain">
      {/* ambient glows */}
      <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[120px]" />
      <div className="absolute bottom-[-15%] left-[-10%] w-[500px] h-[500px] rounded-full bg-indigo-500/10 blur-[120px]" />

      <nav className="relative z-10 max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500 to-indigo-500 flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-extrabold text-base sm:text-lg tracking-tight whitespace-nowrap">The Cognitive <span className="text-cyan-400">Metrics</span></span>
        </div>
        <Link to="/blog" data-testid="nav-blog-link" className="text-sm font-medium text-slate-300 hover:text-cyan-400 transition-colors px-3 py-2 rounded-lg hover:bg-white/5">
          Blog
        </Link>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-10 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/5 mb-7">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="uppercase tracking-[0.25em] text-[11px] font-semibold text-cyan-400">Credited Assessment</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Measure your mind with<br />
            <span className="bg-gradient-to-r from-cyan-400 via-indigo-400 to-cyan-300 bg-clip-text text-transparent">scientific precision.</span>
          </h1>
          <p className="mt-6 text-slate-400 text-base sm:text-lg max-w-lg leading-relaxed">
            A 30-question credited intelligence assessment spanning spatial reasoning, fluid intelligence,
            logic, numerical and verbal comprehension. Unlock a full certified report at the end.
          </p>

          <button
            data-testid="google-login-button"
            onClick={handleLogin}
            className="mt-9 group inline-flex items-center gap-3 bg-white text-slate-900 font-semibold px-6 py-3.5 rounded-xl hover:shadow-[0_0_40px_rgba(6,182,212,0.4)] transition-all duration-300 hover:-translate-y-0.5"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.4 0 10.3-2 14-5.3l-6.5-5.5c-2 1.4-4.6 2.3-7.5 2.3-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5c-.5.4 7-5 7-15 0-1.2-.1-2.3-.4-3.5z"/></svg>
            Sign in with Google to begin
          </button>

          <div className="mt-10 grid grid-cols-3 gap-4 sm:flex sm:flex-wrap sm:gap-8">
            {[
              { n: "500,000+", l: "Tests benchmarked" },
              { n: "99.4%", l: "Matrix engine precision" },
              { n: "30", l: "Credited questions" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-sub font-bold text-xl sm:text-2xl text-white">{s.n}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
            <img src={BRAIN_IMG} alt="Neural network" className="w-full h-[460px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex gap-3">
              <div className="flex-1 backdrop-blur-xl bg-[#0B0F17]/70 border border-white/10 rounded-xl p-3 flex items-center gap-2.5">
                <ShieldCheck className="w-5 h-5 text-cyan-400" />
                <span className="text-xs text-slate-200">Secure &amp; private results</span>
              </div>
              <div className="flex-1 backdrop-blur-xl bg-[#0B0F17]/70 border border-white/10 rounded-xl p-3 flex items-center gap-2.5">
                <Activity className="w-5 h-5 text-amber-400" />
                <span className="text-xs text-slate-200">Percentile benchmarking</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SEO content: what we measure */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20" aria-labelledby="measure-heading">
        <h2 id="measure-heading" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
          A free online IQ test that measures five cognitive domains
        </h2>
        <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">
          The Cognitive Metrics is a credited 30-question intelligence assessment calibrated to the standard
          deviation IQ scale (mean 100, standard deviation 15). In 25 minutes you will complete a balanced mix
          of visual pattern matrices and text-based verbal, numerical and logical items, then receive an exact
          IQ score, a global percentile ranking and an optional verifiable certificate.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
          {[
            { t: "Spatial Reasoning", d: "Mentally rotate and manipulate visual patterns and matrices." },
            { t: "Fluid Intelligence", d: "Spot novel patterns and solve problems without prior knowledge." },
            { t: "Logical Deduction", d: "Draw valid conclusions from premises and rule sets." },
            { t: "Numerical Patterning", d: "Recognise sequences, ratios and numeric relationships." },
            { t: "Verbal Comprehension", d: "Reason with analogies, synonyms and language structure." },
          ].map((c) => (
            <div key={c.t} className="p-5 rounded-2xl bg-[#131B2A] border border-white/10">
              <h3 className="font-semibold text-slate-100">{c.t}</h3>
              <p className="text-sm text-slate-400 mt-1.5">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SEO content: IQ scale explainer */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20" aria-labelledby="scale-heading">
        <h2 id="scale-heading" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
          What is a good IQ score?
        </h2>
        <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">
          IQ scores follow a normal distribution with an average (mean) of 100 and a standard deviation of 15.
          That means about two-thirds of people score between 85 and 115. Here is how the standard IQ
          classification scale breaks down, and where your result on The Cognitive Metrics will place you.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-10">
          {[
            { r: "130+", t: "Very Superior", d: "Top 2% — the gifted range." },
            { r: "120–129", t: "Superior", d: "Top 10% of test-takers." },
            { r: "110–119", t: "High Average", d: "Above the typical range." },
            { r: "90–109", t: "Average", d: "Where most people score." },
            { r: "Below 90", t: "Developing", d: "Room to grow with practice." },
          ].map((c) => (
            <div key={c.t} className="p-5 rounded-2xl bg-[#131B2A] border border-white/10">
              <div className="font-sub font-bold text-2xl text-cyan-400">{c.r}</div>
              <h3 className="font-semibold text-slate-100 mt-1">{c.t}</h3>
              <p className="text-sm text-slate-400 mt-1">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SEO content: inspirational intellectual manifesto */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20" aria-labelledby="greatness-heading">
        <h2 id="greatness-heading" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
          Unlock your cognitive potential and pursue intellectual greatness
        </h2>
        <div className="mt-5 max-w-3xl space-y-4 text-slate-400 leading-relaxed">
          <p>
            Intelligence is not a fixed verdict — it is a frontier. Every mind holds untapped reasoning power
            waiting to be measured, understood and sharpened. The Cognitive Metrics online IQ test turns that
            raw potential into a precise, scientifically benchmarked number, giving ambitious thinkers the
            clarity to pursue genuine mental excellence rather than guess at it.
          </p>
          <p>
            Whether you are preparing for a high-IQ society, practising for a Mensa-style assessment, or simply
            determined to understand how your brain solves problems, a credited IQ score is the first deliberate
            step toward greatness. Knowing your baseline across spatial reasoning, fluid intelligence, logical
            deduction, numerical patterning and verbal comprehension transforms vague ambition into a focused,
            measurable pursuit of a sharper mind.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-10">
          {[
            { t: "Measure your genius", d: "Discover your exact IQ score and global percentile, and see precisely where you stand among the brightest analytical minds worldwide." },
            { t: "Train your reasoning", d: "Challenge your fluid intelligence and problem-solving under pressure — the raw cognitive horsepower behind every great thinker and innovator." },
            { t: "Certify your intellect", d: "Earn a verifiable certificate that proves your cognitive achievement to employers, universities, peers and high-IQ communities." },
          ].map((c) => (
            <div key={c.t} className="p-5 rounded-2xl bg-[#131B2A] border border-white/10">
              <h3 className="font-semibold text-slate-100">{c.t}</h3>
              <p className="text-sm text-slate-400 mt-1.5">{c.d}</p>
            </div>
          ))}
        </div>
        <blockquote className="mt-10 max-w-3xl border-l-2 border-cyan-500/50 pl-5 text-lg text-slate-300 italic">
          “The mind is not a vessel to be filled, but a fire to be kindled.” Greatness is not reserved for the
          few — it is cultivated by those bold enough to measure where they stand and relentless enough to climb
          higher.
        </blockquote>
      </section>

      {/* SEO content: how to improve your IQ */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pb-20" aria-labelledby="improve-heading">
        <h2 id="improve-heading" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
          How to improve your IQ and sharpen your mind
        </h2>
        <p className="mt-4 text-slate-400 max-w-3xl leading-relaxed">
          Cognitive performance responds to deliberate practice. Research on neuroplasticity shows that
          consistent reasoning challenges, pattern recognition drills and problem-solving under time pressure can
          strengthen the mental pathways that an IQ test measures. Treat each assessment as a benchmark: measure,
          reflect on your cognitive breakdown, train your weakest domain, and retake the test to watch your
          percentile climb over time.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {[
            { t: "Benchmark honestly", d: "Establish a true baseline IQ score before you begin improving — you cannot optimise what you have not measured." },
            { t: "Target weak domains", d: "Use your five-dimension cognitive breakdown to focus practice where it moves your score the most." },
            { t: "Think under pressure", d: "Timed reasoning builds the mental speed and composure that separate good thinkers from great ones." },
            { t: "Track your ascent", d: "Retake the assessment and follow your percentile trend to prove measurable intellectual growth." },
          ].map((c) => (
            <div key={c.t} className="p-5 rounded-2xl bg-[#131B2A] border border-white/10">
              <h3 className="font-semibold text-slate-100">{c.t}</h3>
              <p className="text-sm text-slate-400 mt-1.5">{c.d}</p>
            </div>
          ))}
        </div>
      </section>


      {/* SEO content: FAQ (mirrors FAQPage schema) */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 pb-24" aria-labelledby="faq-heading">
        <h2 id="faq-heading" className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
          IQ test — frequently asked questions
        </h2>
        <div className="mt-8 space-y-5">
          {[
            { q: "Is this IQ test scientifically accurate?", a: "The assessment is calibrated to the standard deviation IQ scale (mean 100, SD 15) and measures five cognitive domains: spatial reasoning, fluid intelligence, logical deduction, numerical patterning and verbal comprehension." },
            { q: "Is the IQ test free to take?", a: "Yes, taking the 30-question IQ test is completely free. You only pay if you choose to unlock your detailed report, percentile breakdown and downloadable certificate." },
            { q: "How many questions are in the IQ test?", a: "There are 30 credited questions to complete within a 25-minute limit, combining visual pattern matrices and text-based verbal, numerical and logical items." },
            { q: "How long does the IQ test take?", a: "Most people finish in 15 to 25 minutes. The test has a 25-minute time limit, and you receive your results instantly after submitting." },
            { q: "What is a good IQ score?", a: "On the standard scale, 90–109 is average, 110–119 is high average, 120–129 is superior, and 130 or above is very superior — roughly the top 2% of the population." },
            { q: "What is the average IQ?", a: "The average IQ is 100 by definition. Scores follow a normal distribution with a standard deviation of 15, so about two-thirds of people score between 85 and 115." },
            { q: "What IQ is considered genius?", a: "An IQ of 130 or higher is classed as 'very superior' and places you in roughly the top 2% of test-takers, the threshold often associated with giftedness." },
            { q: "Do I get a certificate with my IQ score?", a: "Yes. The Premium report includes a downloadable PDF certificate and a public verification link so anyone can confirm your certified score." },
            { q: "How is my IQ percentile calculated?", a: "Your score is mapped to a normal distribution (mean 100, SD 15) to produce a global percentile, showing what proportion of test-takers you scored higher than." },
          ].map((f) => (
            <div key={f.q} className="p-5 rounded-2xl bg-[#131B2A] border border-white/10">
              <h3 className="font-semibold text-slate-100">{f.q}</h3>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-4">
            <Link to="/blog" className="text-sm text-slate-400 hover:text-cyan-400 transition-colors">Blog</Link>
            <Link to="/blog/what-is-a-good-iq-score" className="text-sm text-slate-400 hover:text-cyan-400 transition-colors">What is a good IQ score?</Link>
            <Link to="/blog/how-to-improve-your-iq" className="text-sm text-slate-400 hover:text-cyan-400 transition-colors">How to improve your IQ</Link>
            <Link to="/blog/how-to-train-fluid-intelligence" className="text-sm text-slate-400 hover:text-cyan-400 transition-colors">Train fluid intelligence</Link>
          </div>
          <div className="text-center text-sm text-slate-500">
            © {new Date().getFullYear()} The Cognitive Metrics · IQ measured with scientific precision
          </div>
        </div>
      </footer>
    </div>
  );
};
